import * as dc from "@/components/layout/list/defaultType";
import { FormInstance } from "antd";
import { RoastingSeries } from "./roastingSeriesTypes";
import { getRoastingShowTimeCheck, RoastingCheckCount } from "@/components/layout/inputCols/roastingCols";
import { DataInputCommonParams } from "@/components/layout/input/inputType";
import { getInputValuesCheck } from "@/components/layout/input/inputFunction";
import { checkEmpty, getJsonFromStr } from "@/util/stringutil";
import { calcThermalExposure } from "../lossModel/thermalExposure";

//원시 설명력 스코어
function calcRawLossRatio(actualLoss: number, duration: number, thermalExposure: number): number {
  if (duration <= 0 || thermalExposure <= 0) return 0;

  return actualLoss / (duration * thermalExposure);
}
//정규화
function normalizeRatio(value: number, min: number, max: number): number {
  if (max === min) return 0.5;
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

export function getRoastingSeriesInput(
  form: FormInstance<any> | undefined,
  formMemo: Record<string, any> | undefined,
  dataFunction: Record<string, Function> | undefined,
) {
  const roastingTimes = getRoastingShowTimeCheck(1, RoastingCheckCount);
  const roastingTimeList = Object.keys(roastingTimes).filter((item) => item.indexOf("pre_") !== 0);
  const roastingTimeFields: Record<string, DataInputCommonParams> = {
    roasting_loss: dc.lossRate,
    roasting_chart_log: dc.textArea,
  };

  roastingTimeList.forEach((item) => {
    if (item === "roasting_start_time") {
      roastingTimeFields[item] = dc.roastingTime;
    } else {
      roastingTimeFields[item] = dc.timer;
    }
    roastingTimeFields[roastingTimes[item].bean] = dc.roastingTemp;
  });
  const series_values = getInputValuesCheck(form, formMemo, dataFunction, undefined, roastingTimeFields, true);
  return getRoastingSeries(series_values, undefined, roastingTimes, roastingTimeList);
}

export function getRoastingSeries(
  values: any,
  id?: string | undefined,
  times?: Record<string, Record<string, string>> | undefined,
  timeList?: string[] | undefined,
) {
  const roastingTimes = times ?? getRoastingShowTimeCheck(1, RoastingCheckCount);
  const roastingTimeList = timeList ?? Object.keys(roastingTimes).filter((item) => item.indexOf("pre_") !== 0);
  const roastingSeries: RoastingSeries = {
    id,
    series: [],
    turningEndTime: -1,
    turningEndBT: -1,
    dryEndTime: -1,
    dropTime: -1,
    dryEndBT: -1,
    actualLoss: values.roasting_loss ? values.roasting_loss / 1000 : 0,
  };
  roastingSeries.series = roastingTimeList
    .map((item) => {
      const time = item === "roasting_start_time" ? 0 : values[item] ? values[item] / 1000 : undefined;
      const bean = values[roastingTimes[item].bean] !== undefined ? values[roastingTimes[item].bean] / 100 : undefined;

      if (time !== undefined && bean !== undefined) {
        if (item === "roasting_turning_term") {
          roastingSeries.turningEndTime = time;
          roastingSeries.turningEndBT = bean;
        } else if (item === "roasting_dried_term") {
          roastingSeries.dryEndTime = time;
          roastingSeries.dryEndBT = bean;
        } else if (item === "roasting_end_term") {
          roastingSeries.dropTime = time;
        }
        return { time, bt: bean, event: item };
      }
      return undefined;
    })
    .filter((item) => item != undefined);
  const roastingLogs = values.roasting_chart_log ? getJsonFromStr(values.roasting_chart_log) : undefined;
  if (!checkEmpty(roastingLogs)) {
    Object.keys(roastingLogs).forEach((item) => {
      const time = Number(item);
      const bean = roastingLogs[item].bean;
      if (
        time !== undefined &&
        time > 0 &&
        bean !== undefined &&
        roastingSeries.dropTime &&
        (roastingSeries.dropTime < 0 || (roastingSeries.dropTime > 0 && roastingSeries.dropTime >= time / 1000))
      ) {
        roastingSeries.series.push({ time: time / 1000, bt: bean, event: "log" });
      }
    });
  }
  roastingSeries.series.sort((a, b) => a.time - b.time);
  return roastingSeries;
}

export function getRoastingSeriesCalcLossRatio(roastingSeries: RoastingSeries) {
  return calcLossCorrelationRatio({ ...roastingSeries, scale: 1000000 });
}
export function calcLossCorrelationRatio(
  params: RoastingSeries & {
    scale?: number;
    ratioMin?: number;
    ratioMax?: number;
  },
) {
  const duration = params.dropTime && params.dryEndTime ? params.dropTime - params.dryEndTime : 0;

  const { exposure, max } =
    params.turningEndTime && params.dropTime && params.dryEndBT
      ? calcThermalExposure(params.series, params.turningEndTime, params.dropTime, params.dryEndBT)
      : { exposure: 0, max: 0 };

  const area = params.dropTime ? (params.dropTime * max) / 2 : 0;
  const loss = params.actualLoss ?? 0;
  const lossSqrt = Math.sqrt(loss);
  const raw = calcRawLossRatio(loss, duration, exposure) * (params.scale ?? 1);
  const rawSqrt = calcRawLossRatio(lossSqrt, duration, exposure) * (params.scale ?? 1);

  return {
    area,
    max,
    loss,
    yield: 1 - loss,
    // lossSqrt,
    raw:
      params.ratioMin && params.ratioMax
        ? normalizeRatio(raw, params.ratioMin, params.ratioMax)
        : Math.round(raw * 10000) / 100,
    rawSqrt:
      params.ratioMin && params.ratioMax
        ? normalizeRatio(rawSqrt, params.ratioMin, params.ratioMax)
        : Math.round(rawSqrt * 10000) / 100,
    calc1: Math.round((area * loss) / 10) / 100,
    calc2: Math.round((area * lossSqrt) / 10) / 100,
    calc3: loss === 0 ? 0 : Math.round(area / loss / 100) / 100,
    calc4: loss === 0 ? 0 : Math.round(area / lossSqrt / 100) / 100,
    calc5: loss === 0 ? 0 : Math.round(area / Math.sqrt(Math.sqrt(lossSqrt)) / 100) / 100,
  };
}
